import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import MultipleSelector, { Option } from '@/components/ui/multiple-selector';
import { apiFetch } from '../utils/api';
import { useI18n } from '../hooks/useI18n';



interface CostCenter {
    id: string;
    name: string;
}

interface Currency {
    code: string;
    name: string;
}

interface Vendor {
    id: string;
    name: string;
}

interface Category {
    id: string;
    name: string;
}

interface AdvancedFilters {
    cost_center_id?: string;
    date_from?: string;
    date_to?: string;
    due_date_from?: string;
    due_date_to?: string;
    payment_date_from?: string;
    payment_date_to?: string;
    currency?: string;
    category_ids?: string[];
    category_exclude?: string;
    min_amount?: string;
    max_amount?: string;
    vendor_ids?: string[];
    customer_id?: string;
    payment_status?: string;
    has_photo?: string;
}

interface InvoiceAdvancedFilterProps {
    filters: AdvancedFilters;
    onFiltersChange: (filters: AdvancedFilters) => void;
    onClear: () => void;
    isOutbound?: boolean; // Flag to indicate if this is for outbound invoices (sales)
    isInbound?: boolean; // Flag to indicate if this is for inbound invoices (purchases)
}

export default function InvoiceAdvancedFilter({
    filters,
    onFiltersChange,
    onClear,
    isOutbound = false,
    isInbound = false
}: InvoiceAdvancedFilterProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const { t } = useI18n();

    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [customers, setCustomers] = useState<Vendor[]>([]);

    // Decode common HTML entities that might be present in vendor names (e.g., &quot;)
    const decodeHtml = (s: string | undefined): string => {
        if (!s) return '';
        // Use a simple replacement fallback for SSR safety
        const replaced = s
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>');
        if (typeof window === 'undefined') return replaced;
        try {
            const textarea = document.createElement('textarea');
            textarea.innerHTML = replaced;
            return textarea.value;
        } catch {
            return replaced;
        }
    };

    // Load dropdown data
    useEffect(() => {
        const loadData = async () => {
            try {
                // Build vendors URL with filter based on invoice direction
                let vendorsUrl = '/api/vendors/?limit=100';
                let customersUrl = '/api/vendors/?limit=100';

                if (isOutbound) {
                    // For outbound invoices, only need customers (companies we sold to)
                    customersUrl = '/api/vendors/?limit=100&used_as_customer=true';
                } else if (isInbound) {
                    // For inbound invoices, need both vendors (suppliers) and customers (our companies that received invoices)
                    vendorsUrl = '/api/vendors/?limit=100&used_as_vendor=true';
                    // For inbound invoices, customer_id refers to which of our companies received the invoice
                    customersUrl = '/api/vendors/?limit=100&used_as_customer_inbound=true';
                }

                const requests = [
                    apiFetch('/api/cost-centers/?limit=100'),
                    apiFetch('/api/currencies/'),
                    apiFetch(customersUrl),
                    apiFetch('/api/categories/')
                ];

                // Only fetch vendors separately if we're on inbound page
                if (isInbound) {
                    requests.push(apiFetch(vendorsUrl));
                }

                const responses = await Promise.all(requests);
                const [costCentersRes, currenciesRes, customersRes, categoriesRes, vendorsRes] = responses;

                if (costCentersRes.ok) {
                    const costCentersData = await costCentersRes.json();
                    // The API returns a direct array for cost centers
                    setCostCenters(Array.isArray(costCentersData) ? costCentersData : (costCentersData.cost_centers || []));
                }

                if (currenciesRes.ok) {
                    const currenciesData = await currenciesRes.json();
                    setCurrencies(currenciesData || []);
                }

                if (customersRes.ok) {
                    const customersData = await customersRes.json();
                    setCustomers(customersData.vendors || []);
                }

                if (categoriesRes.ok) {
                    const categoriesData = await categoriesRes.json();
                    setCategories(Array.isArray(categoriesData) ? categoriesData : []);
                }

                if (isInbound && vendorsRes && vendorsRes.ok) {
                    const vendorsData = await vendorsRes.json();
                    setVendors(vendorsData.vendors || []);
                }
            } catch (error) {
                console.error('Error loading filter data:', error);
            }
        };

        loadData();
    }, [isOutbound, isInbound]);

    const handleFilterChange = (key: keyof AdvancedFilters, value: any) => {
        onFiltersChange({
            ...filters,
            [key]: value
        });
    };

    const hasActiveFilters = Object.values(filters).some(value =>
        value !== undefined && value !== '' &&
        (Array.isArray(value) ? value.length > 0 : true)
    );

    const selectedCostCenterName = costCenters.find(c => c.id === filters.cost_center_id)?.name;
    const selectedCustomerName = customers.find(v => v.id === filters.customer_id)?.name;
    const selectedVendorName = filters.vendor_ids && filters.vendor_ids.length === 1
        ? vendors.find(v => v.id === filters.vendor_ids![0])?.name
        : undefined;
    const categoryOptions: Option[] = categories.map((category) => ({
        value: category.id,
        label: category.name,
    }));
    const selectedCategoryOptions: Option[] = (filters.category_ids || [])
        .map((categoryId) => {
            const category = categories.find((item) => item.id === categoryId);
            return {
                value: categoryId,
                label: category?.name || categoryId,
            };
        });

    return (
        <div className="border rounded-lg bg-white">
            {/* Header */}
            <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-2">
                    {isExpanded ? (
                        <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                    )}
                    <span className="font-medium">{t('actions.advancedFilters', 'common')}</span>
                    {hasActiveFilters && (
                        <Badge variant="secondary" className="ml-2">
                            {t('status.active', 'common')}
                        </Badge>
                    )}
                </div>
                {hasActiveFilters && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClear();
                        }}
                        className="text-sm"
                    >
                        {t('actions.clearAll', 'common')}
                    </Button>
                )}
            </div>

            {/* Filter Content */}
            {isExpanded && (
                <div className="border-t p-4 space-y-4">
                    {/* All Filters in Multiple Rows */}
                    <div className="flex flex-wrap gap-4 items-start">
                        {/* Cost Center */}
                        {isInbound && (
                            <div className="space-y-2 text-left">
                            <Label htmlFor="cost-center">{t('invoices.costCenter', 'finance')}</Label>
                            <Select
                                value={filters.cost_center_id || 'all'}
                                onValueChange={(value) =>
                                    handleFilterChange('cost_center_id', value === 'all' ? undefined : value)
                                }
                            >
                                <SelectTrigger className="text-left truncate min-w-0 max-w-[360px] w-64" title={selectedCostCenterName ? decodeHtml(selectedCostCenterName) : undefined}>
                                    <SelectValue placeholder="Select cost center" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('actions.all', 'common')}</SelectItem>
                                    {costCenters.map((center) => (
                                        <SelectItem key={center.id} value={center.id}>
                                            {center.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            </div>
                        )}

                        {/* Currency */}
                        <div className="space-y-2 text-left">
                            <Label htmlFor="currency">{t('invoices.currency', 'finance')}</Label>
                            <Select
                                value={filters.currency || 'all'}
                                onValueChange={(value) =>
                                    handleFilterChange('currency', value === 'all' ? undefined : value)
                                }
                            >
                                <SelectTrigger className="text-left">
                                    <SelectValue placeholder="Select currency" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('actions.all', 'common')}</SelectItem>
                                    {currencies.map((currency) => (
                                        <SelectItem key={currency.code} value={currency.code}>
                                            {currency.code} - {currency.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Categories */}
                        <div className="space-y-2 text-left w-auto min-w-[460px] max-w-[620px]">
                            <Label>{t('forms.category', 'common')}</Label>
                            <div className="flex items-center gap-2">
                                <div className="flex-1 min-w-0">
                                    <MultipleSelector
                                        value={selectedCategoryOptions}
                                        onChange={(selectedOptions) =>
                                            handleFilterChange(
                                                'category_ids',
                                                selectedOptions.length > 0
                                                    ? selectedOptions.map((option) => option.value)
                                                    : undefined
                                            )
                                        }
                                        options={categoryOptions}
                                        className="h-9 min-h-9 py-0 items-center [&>div]:items-center [&>div]:max-h-none [&>div]:overflow-y-hidden [&_input]:self-center"
                                        inputProps={{ className: 'self-center' }}
                                        placeholder={t('categories.searchCategories', 'common')}
                                        emptyIndicator={
                                            <p className="text-center text-sm leading-10 text-muted-foreground">
                                                {t('categories.noCategories', 'common')}
                                            </p>
                                        }
                                    />
                                </div>
                                Exclude <Checkbox
                                    id="category-exclude"
                                    checked={filters.category_exclude === 'true'}
                                    onCheckedChange={(checked) =>
                                        handleFilterChange('category_exclude', checked ? 'true' : undefined)
                                    }
                                    aria-label="Exclude categories"
                                    title="Exclude categories"
                                />
                            </div>
                        </div>

                        {/* Date From */}
                        <div className="space-y-2 text-left">
                            <Label htmlFor="date-from">{t('forms.dateFrom', 'common')}</Label>
                            <Input
                                id="date-from"
                                type="date"
                                className="text-left"
                                value={filters.date_from || ''}
                                onChange={(e) =>
                                    handleFilterChange('date_from', e.target.value || undefined)
                                }
                            />
                        </div>

                        {/* Date To */}
                        <div className="space-y-2 text-left">
                            <Label htmlFor="date-to">{t('forms.dateTo', 'common')}</Label>
                            <Input
                                id="date-to"
                                type="date"
                                className="text-left"
                                value={filters.date_to || ''}
                                onChange={(e) =>
                                    handleFilterChange('date_to', e.target.value || undefined)
                                }
                            />
                        </div>

                        {/* Due Date From */}
                        <div className="space-y-2 text-left">
                            <Label htmlFor="due-date-from">{t('invoices.dueDateFrom', 'finance')}</Label>
                            <Input
                                id="due-date-from"
                                type="date"
                                className="text-left"
                                value={filters.due_date_from || ''}
                                onChange={(e) =>
                                    handleFilterChange('due_date_from', e.target.value || undefined)
                                }
                            />
                        </div>

                        {/* Due Date To */}
                        <div className="space-y-2 text-left">
                            <Label htmlFor="due-date-to">{t('invoices.dueDateTo', 'finance')}</Label>
                            <Input
                                id="due-date-to"
                                type="date"
                                className="text-left"
                                value={filters.due_date_to || ''}
                                onChange={(e) =>
                                    handleFilterChange('due_date_to', e.target.value || undefined)
                                }
                            />
                        </div>

                        {/* Payment Date From */}
                        <div className="space-y-2 text-left">
                            <Label htmlFor="payment-date-from">{t('invoices.paymentDateFrom', 'finance')}</Label>
                            <Input
                                id="payment-date-from"
                                type="date"
                                className="text-left"
                                value={filters.payment_date_from || ''}
                                onChange={(e) =>
                                    handleFilterChange('payment_date_from', e.target.value || undefined)
                                }
                            />
                        </div>

                        {/* Payment Date To */}
                        <div className="space-y-2 text-left">
                            <Label htmlFor="payment-date-to">{t('invoices.paymentDateTo', 'finance')}</Label>
                            <Input
                                id="payment-date-to"
                                type="date"
                                className="text-left"
                                value={filters.payment_date_to || ''}
                                onChange={(e) =>
                                    handleFilterChange('payment_date_to', e.target.value || undefined)
                                }
                            />
                        </div>

                        {/* Min Amount */}
                        <div className="space-y-2 text-left">
                            <Label htmlFor="min-amount">{t('units.min', 'common')} {t('units.amount', 'common')}</Label>
                            <Input
                                id="min-amount"
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                className="text-left"
                                value={filters.min_amount || ''}
                                onChange={(e) =>
                                    handleFilterChange('min_amount', e.target.value ? String(e.target.value) : undefined)
                                }
                            />
                        </div>

                        {/* Max Amount */}
                        <div className="space-y-2 text-left">
                            <Label htmlFor="max-amount">{t('units.max', 'common')} {t('units.amount', 'common')}</Label>
                            <Input
                                id="max-amount"
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                className="text-left"
                                value={filters.max_amount || ''}
                                onChange={(e) =>
                                    handleFilterChange('max_amount', e.target.value ? String(e.target.value) : undefined)
                                }
                            />
                        </div>

                        {/* Payment Status */}
                        <div className="space-y-2 text-left">
                            <Label htmlFor="payment-status">{t('invoices.paymentStatus', 'finance')}</Label>
                            <Select
                                value={filters.payment_status || 'all'}
                                onValueChange={(value) =>
                                    handleFilterChange('payment_status', value === 'all' ? undefined : value)
                                }
                            >
                                <SelectTrigger className="text-left">
                                    <SelectValue placeholder="Select payment status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('actions.all', 'common')}</SelectItem>
                                    <SelectItem value="unpaid">{t('invoices.unpaid', 'finance')}</SelectItem>
                                    <SelectItem value="paid">{t('invoices.paid', 'finance')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Vendor filter (for inbound invoices) */}
                        {isInbound && (
                            <div className="space-y-2 text-left">
                                <Label htmlFor="vendor">{t('invoices.vendor', 'finance')}</Label>
                                <Select
                                    value={filters.vendor_ids && filters.vendor_ids.length === 1 ? filters.vendor_ids[0] : 'all'}
                                    onValueChange={(value) =>
                                        handleFilterChange('vendor_ids', value === 'all' ? undefined : [value])
                                    }
                                >
                                    <SelectTrigger className="text-left truncate min-w-0 w-64 max-w-[360px]" title={selectedVendorName ? decodeHtml(selectedVendorName) : undefined}>
                                        <SelectValue placeholder="Select vendor" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('actions.all', 'common')}</SelectItem>
                                        {vendors.map((v) => (
                                            <SelectItem key={v.id} value={v.id}>
                                                {decodeHtml(v.name)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Customer filter (always shown) */}
                        <div className="space-y-2 text-left">
                            <Label htmlFor="customer">{t('invoices.customer', 'finance')}</Label>
                            <Select
                                value={filters.customer_id || 'all'}
                                onValueChange={(value) =>
                                    handleFilterChange('customer_id', value === 'all' ? undefined : value)
                                }
                            >
                                <SelectTrigger className="text-left truncate min-w-0 max-w-[360px] w-64" title={selectedCustomerName ? decodeHtml(selectedCustomerName) : undefined}>
                                    <SelectValue placeholder="Select customer" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('actions.all', 'common')}</SelectItem>
                                    {customers.map((v) => (
                                        <SelectItem key={v.id} value={v.id}>
                                            {decodeHtml(v.name)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>


                        {/* Has Photo */}
                        <div className="space-y-2 text-left">
                            <Label htmlFor="has-photo">{t('invoices.hasAttachment', 'finance')}</Label>
                            <Select
                                value={filters.has_photo || 'all'}
                                onValueChange={(value) =>
                                    handleFilterChange('has_photo', value === 'all' ? undefined : value)
                                }
                            >
                                <SelectTrigger className="text-left">
                                    <SelectValue placeholder="Select attachment status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t('actions.all', 'common')}</SelectItem>
                                    <SelectItem value="true">{t('actions.with', 'common')} {t('invoices.hasAttachment', 'finance')}</SelectItem>
                                    <SelectItem value="false">{t('actions.without', 'common')} {t('invoices.hasAttachment', 'finance')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
